import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import ReactHtmlParser from 'react-html-parser';
import Validate from 'app/validation/validation';
import { executeWithCaptcha, getMediaPath, reactParserCommonOptions } from '../../../../app/functions';
import { addPopup, setUserApplication } from "../../../../redux/actions";
import * as Popups from '../../../../constants/popup-types';
import AnalyticsManager from '../../../../app/analytics';

//components
import AnimatedInput from "components/forms/animated_input";
import AnimatedTextArea from "components/forms/animated_textarea";
import AutoComplete from "../../../../components/forms/autocomplete";
import Checkbox from "../../../../components/forms/checkbox";

//assets
import './join-form.scss';
import subscribe_banner from 'assets/mock/subscribe-client-banner.jpg';
import arrow from "assets/icons/arrow_down.svg";
import Select from "../../../../components/forms/select";
import paper_plane from 'assets/icons/paper-plane.svg';
import Recaptcha from "../../../../components/services/google/recaptcha";
import checkedCheckboxImagePath from "../../../../assets/icons/cb_checked.svg";
import emptyCheckboxImagePath from "../../../../assets/icons/cb_empty.svg";

function JoinForm({ joiningReasons, joiningRoutes, workplaces, title, userApplicationPage, deviceState }) {
    const [formState, setFormState] = useState({
        first_try: true,
        form: {
            first_name: { val: '', valid: false, rules: ['first_name', 'not_empty'], errMsg: '' },
            last_name: { val: '', valid: false, rules: ['last_name', 'not_empty'], errMsg: '' },
            id: { val: '', valid: false, rules: ['id', 'not_empty'], errMsg: '' },
            phone: { val: '', valid: false, rules: ['phone', 'not_empty'], errMsg: '' },
            email: { val: '', valid: false, rules: ['email', 'not_empty'], errMsg: '' },
            message: { val: '', valid: false, rules: [], errMsg: '' },
            contact_agreement: { val: false, valid: false, rules: ['checked_checkbox'], errMsg: '' },
        },
        business: { errMsg: '', selected: -1, valid: false, query: '' },
        reason: { selected: -1, errMsg: '', valid: false },
        route: { selected: -1, errMsg: '', valid: false },
        isButtonClicked: false,
    });

    const isMobileOrTablet = deviceState === 'mobile' || deviceState === 'tablet';
    const text = useSelector(store => store.config.Translations);
    const leadStatus = useSelector(store => store.data.UserAppStatus);
    const dispatch = useDispatch();

    useEffect(() => {
        if (leadStatus) {
            try {
                const form = formState.form;
                AnalyticsManager.logLeadUser({
                    personal_id: form.id.val,
                    first_name: form.first_name.val,
                    last_name: form.last_name.val,
                    email: form.email.val,
                    cellphone: form.phone.val,
                    reason: joiningReasons[formState.reason.selected].title,
                });
            } catch (e) {
                console.error(e);
            }
        }
    }, [leadStatus, formState]);

    const handleInputChange = (evt) => {
        const { value, name } = evt.target;
        if (name === 'full_name' && value !== '') {
            if (!(/^[A-Za-z\u0590-\u05fe\s]+$/.test(value))) return;
        }

        let validate = Validate(value, formState.form[name].rules);
        let is_valid = validate.valid;
        let msg = validate.msg;
        setFormState(prevState => ({
            ...prevState,
            form: {
                ...prevState.form,
                [name]: { ...prevState.form[name], val: value, valid: is_valid, errMsg: msg }
            }
        }));
    };

    const handleContactAgreementChange = (newValue) => {
        const validation = Validate(newValue, formState.form.contact_agreement.rules);

        setFormState(prevState => ({
            ...prevState,
            form: {
                ...prevState.form,
                contact_agreement: {
                    ...prevState.form.contact_agreement,
                    val: newValue,
                    valid: validation.valid,
                    errMsg: validation.msg,
                },
            },
        }));
    };

    const clearBusinessAutocomplete = () => {
        setFormState(prevState => ({
            ...prevState,
            business: { ...prevState.business, query: '' }
        }));
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        let form_valid = true;
        let new_state = { ...formState.form };

        for (let field in formState.form) {
            let validate = Validate(formState.form[field].val, formState.form[field].rules);
            let is_valid = validate.valid;
            let msg = validate.msg;

            new_state[field].valid = is_valid;
            new_state[field].errMsg = msg;

            if (is_valid === false) {
                form_valid = false;
            }
        }

        const reason = formState.reason.selected !== -1;
        const route = formState.route.selected !== -1;
        const business = formState.business.selected !== -1;

        if (!reason) {
            setFormState(prevState => ({
                ...prevState,
                reason: { ...prevState.reason, valid: false }
            }));
            form_valid = false;
        }

        if (!route) {
            setFormState(prevState => ({
                ...prevState,
                route: { ...prevState.route, valid: false }
            }));
            form_valid = false;
        } else {
            if (!business && showWorkPlace()) {
                setFormState(prevState => ({
                    ...prevState,
                    business: { ...prevState.business, errMsg: 'אנא בחרו נושא' }
                }));
                form_valid = false;
            }
        }

        setFormState(prevState => ({
            ...prevState,
            form: new_state,
            first_try: false
        }));

        if (form_valid) {
            if (!formState.isButtonClicked) {
                setFormState(prevState => ({
                    ...prevState,
                    isButtonClicked: true
                }));
            }
            executeWithCaptcha((token) => {
                let payload = {};
                for (let key in formState.form) {
                    payload[key] = formState.form[key].val;
                }

                payload['reason'] = formState.reason.selected;
                payload['route'] = formState.route.selected;
                payload['g-recaptcha-response'] = token;

                if (showWorkPlace()) {
                    payload['workPlaceId'] = formState.business.selected;
                }

                dispatch(addPopup({
                    type: Popups.FILE_UPLOAD_POPUP,
                    text: joiningReasons[formState.reason.selected].FormApproveText,
                    payload: payload,
                }));
            });
        }
    };

    const getAllBusinesses = () => {
        return workplaces.map(item => ({ text: item.text, id: item.id }));
    };

    const businessOnSelected = (id, text) => {
        setFormState(prevState => ({
            ...prevState,
            business: { ...prevState.business, selected: id, errMsg: '', valid: true, query: text }
        }));
    };

    const businessOnChange = (e) => {
        setFormState(prevState => ({
            ...prevState,
            business: { ...prevState.business, query: e.target.value }
        }));
    };

    const getAllReasons = () => {
        let res = [];
        for (let key in joiningReasons) {
            const item = joiningReasons[key];
            res.push({ id: key, text: item.title, showWorkPlaces: item.showWorkPlaces });
        }
        return res;
    };

    const getAllRoutes = () => {
        let res = [];
        for (let key in joiningRoutes) {
            const item = joiningRoutes[key];
            res.push({ id: key, text: item.title });
        }
        return res;
    };

    const showWorkPlace = () => {
        let res = false;
        if (formState.reason.selected !== -1)
            res = joiningReasons[formState.reason.selected]?.showWorkplaceSelection;
        return res;
    };

    const showErrorFirstName = !formState.first_try && !formState.form.first_name.valid;
    const showErrorLastName = !formState.first_try && !formState.form.last_name.valid;
    const showErrorEmail = !formState.first_try && !formState.form.email.valid;
    const showErrorPhone = !formState.first_try && !formState.form.phone.valid;
    const showErrorMassage = !formState.first_try && !formState.form.message.valid;
    const showErrorSubject = !formState.first_try && !formState.business.valid;
    const showErrorId = !formState.first_try && !formState.form.id.valid;
    const showErrorContactAgreement = !formState.first_try && !formState.form.contact_agreement.valid;
    const showErrorReason = !formState.first_try && !formState.reason.valid;
    const showErrorRoute = !formState.first_try && !formState.route.valid;

    return (
        <section className='join-form'>
            <div className='form-wrap'>
                {!leadStatus &&
                    <>
                        <div className="title">{title}</div>
                        <form className='contact-form' onSubmit={handleSubmit}>
                            <div className={'select-wrapper ' + (showErrorReason ? 'err' : '')}>
                                <Select placeholder={text['client_joining_form_reason']}
                                        options={getAllReasons()}
                                        deviceState='desktop'
                                        arrow={getMediaPath(arrow)}
                                        className='reason-select'
                                        onChange={(id) => setFormState(prevState => ({
                                            ...prevState,
                                            reason: {
                                                ...prevState.reason,
                                                selected: id,
                                                valid: true
                                            }
                                        }))}/>
                                {showErrorReason &&
                                    <span className='err-msg'>{text['joining_clients_reason_err']}</span>}
                            </div>

                            {formState.reason !== -1 && showWorkPlace() &&
                                <div className={'select_input ' + (showErrorSubject ? 'error' : '')}>
                                    <AutoComplete placeholder={text.web_business_select_placeholder}
                                                  options={getAllBusinesses()}
                                                  query={formState.business.query}
                                                  onSelect={businessOnSelected}
                                                  sortByDefault={false}
                                                  onChange={businessOnChange}
                                                  onClear={clearBusinessAutocomplete}
                                    />
                                    <span className='error_msg'>{formState.business.errMsg}</span>
                                </div>
                            }

                            <div className='desktop-display'>
                                <AnimatedInput className={'field name'}
                                               value={formState.form.first_name.val}
                                               name={'first_name'}
                                               placeholder={text.order_smart_wallet_placeholder_first_name}
                                               onChange={handleInputChange}
                                               showError={showErrorFirstName}
                                               errorMessage={formState.form.first_name.errMsg}
                                />
                                <AnimatedInput className={'field name'}
                                               value={formState.form.last_name.val}
                                               name={'last_name'}
                                               placeholder={text.order_smart_wallet_placeholder_last_name}
                                               onChange={handleInputChange}
                                               showError={showErrorLastName}
                                               errorMessage={formState.form.last_name.errMsg}
                                />
                            </div>
                            <div className='desktop-display'>
                                <AnimatedInput className={'field id'}
                                               value={formState.form.id.val}
                                               name={'id'}
                                               placeholder={text.web_id_placeholder}
                                               onChange={handleInputChange}
                                               showError={showErrorId}
                                               errorMessage={formState.form.id.errMsg}
                                />
                                <AnimatedInput className={'field phone'}
                                               value={formState.form.phone.val}
                                               name={'phone'}
                                               placeholder={text.web_phone_placeholder}
                                               onChange={handleInputChange}
                                               showError={showErrorPhone}
                                               errorMessage={formState.form.phone.errMsg}
                                />
                            </div>
                            <AnimatedInput className={'field email'}
                                           value={formState.form.email.val}
                                           name={'email'}
                                           placeholder={text.web_burger_contact_placeholder}
                                           onChange={handleInputChange}
                                           showError={showErrorEmail}
                                           errorMessage={formState.form.email.errMsg}
                            />
                            <div className={'select-wrapper ' + (showErrorReason ? 'err' : '')}>
                                <Select
                                    placeholder={text['choose_join_route']}
                                    options={getAllRoutes()}
                                    deviceState='desktop'
                                    arrow={getMediaPath(arrow)}
                                    className='reason-select'
                                    onChange={(selectedOption) => {
                                        setFormState(prevState => ({
                                            ...prevState,
                                            route: {
                                                ...prevState.route,
                                                selected: selectedOption,
                                                valid: true,
                                            },
                                        }));
                                    }}
                                />
                                {showErrorRoute &&
                                    <span className='err-msg'>{text['joining_clients_route_err']}</span>}
                            </div>
                            <AnimatedTextArea className={'field'}
                                              value={formState.form.message.val}
                                              name={'message'}
                                              placeholder={text.web_your_message_placeholder}
                                              onChange={handleInputChange}
                                              showError={showErrorMassage}
                                              errorMessage={formState.form.message.errMsg}
                            />

                            <Checkbox
                                name="contact_agreement"
                                label={ReactHtmlParser(userApplicationPage.details.body.checkboxAgreementText, reactParserCommonOptions())}
                                onChange={handleContactAgreementChange}
                                checked_img={checkedCheckboxImagePath}
                                not_checked_img={emptyCheckboxImagePath}
                                showError={showErrorContactAgreement}
                                errorMessage={formState.form.contact_agreement.errMsg}
                                className="contact-agreement-checkbox"
                            />

                            <button type='submit'
                                    name="submit"
                                    className='submit-btn'
                                    disabled={formState.isButtonClicked}
                                    style={formState.isButtonClicked ? {
                                        opacity: 0.5,
                                        pointerEvents: 'none'
                                    } : { opacity: 1 }}>
                                {text.web_send_label}
                            </button>
                            <div className="legal-notes-wrapper">
                                <span className="legal-notes">{userApplicationPage.details.body.legalNotes}</span>
                            </div>
                        </form>
                        <Recaptcha isFunction={false} enterprise={true} />
                    </>
                }
                {leadStatus &&
                    <div className='sent-lead'>
                        <img src={getMediaPath(paper_plane)} alt='' />
                        <span className='sent-lead-title'>{text['landing_lead_sent_title']}</span>
                        <div className='msg'>{text['joining_client_seccess_msg']}</div>
                    </div>
                }
            </div>
            {!isMobileOrTablet &&
                <img className='back-banner' src={getMediaPath(subscribe_banner)} alt='/' />
            }
        </section>
    );
}

export default JoinForm;
